import type {
  OverrideActionHandler,
  UseInputListboxFunctionality,
  UseInputListboxFunctionalityParams,
} from '@fluentui-copilot/react-input-listbox';
import { useInputListboxFunctionality } from '@fluentui-copilot/react-input-listbox';
import type { PositioningProps } from '@fluentui/react-components';
import { usePositioning } from '@fluentui/react-positioning';
import * as React from 'react';
import type { GroundingMenuProps } from '../GroundingMenu';
import type { GroundingMenuListboxProps } from '../GroundingMenuListbox';

export type { OptionOnSelectData } from '@fluentui-copilot/react-input-listbox';
export type UseGroundingMenuFunctionalityProps = Omit<
  UseInputListboxFunctionalityParams,
  'selectedOptions' | 'defaultSelectedOptions'
> & {
  positioning?: PositioningProps;
};
export type UseGroundingMenuFunctionalityReturn<
  PositioningTargetElem extends HTMLElement,
  ControlSurfaceElem extends HTMLElement,
> = {
  positioningTargetRef: React.MutableRefObject<PositioningTargetElem | null>;
  groundingMenuProps: GroundingMenuProps & { ref: React.MutableRefObject<HTMLDivElement | null> };
  groundingMenuListboxProps: GroundingMenuListboxProps & { ref: React.Ref<HTMLDivElement> };
  controlSurfaceProps: Omit<UseInputListboxFunctionality<ControlSurfaceElem>['triggerProps'], 'ref'> & {
    ref: React.MutableRefObject<ControlSurfaceElem | null>;
  };
};

const defaultPositioningProps: PositioningProps = {
  position: 'above',
  align: 'start',
  fallbackPositions: ['above'],
  offset: { crossAxis: 0, mainAxis: 12 },
  matchTargetSize: 'width',
  autoSize: true,
};

export const useGroundingMenuFunctionality = <
  PositioningTargetElem extends HTMLElement,
  ControlSurfaceElem extends HTMLElement,
>(
  props: UseGroundingMenuFunctionalityProps,
): UseGroundingMenuFunctionalityReturn<PositioningTargetElem, ControlSurfaceElem> => {
  const { positioning = defaultPositioningProps, overrideActionHandler: userOverrideActionHandler, ...rest } = props;

  const overrideActionHandler: OverrideActionHandler = React.useCallback(
    (e, options) => {
      const userAction = userOverrideActionHandler?.(e, options);
      // By default Tab is configured to select the currently focused item.
      // For the GroundingMenu, Tab shouldn't do anything
      const localAction = e.key === 'Tab' ? 'None' : undefined;

      // If the user provided a different override, prefer that.
      const action = userAction?.action ?? localAction;
      const isInSelectionMode = userAction?.isInSelectionMode;

      return { action, isInSelectionMode };
    },
    [userOverrideActionHandler],
  );

  const { triggerProps, inputListboxProps } = useInputListboxFunctionality<ControlSurfaceElem>({
    ...rest,
    overrideActionHandler,
  });

  const { containerRef, targetRef } = usePositioning(positioning);

  return {
    positioningTargetRef: targetRef,
    groundingMenuProps: {
      ref: containerRef,
    },
    groundingMenuListboxProps: inputListboxProps,
    controlSurfaceProps: triggerProps,
  };
};
